import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import {
  PrecisionManufacturing as MachineIcon,
  AddRounded,
  DragIndicatorOutlined,
  MoreVertOutlined,
  OpenInNewOutlined,
} from "@mui/icons-material";
import { Draggable } from "@fullcalendar/interaction";
import { useNavigate } from "react-router-dom";
import { WorkOrder, WorkOrderStatusCode } from "../../models/models";
import AssigneeAvatarStack from "../shared/AssigneeAvatarStack";
import useTranslation from "../../../../shared/hooks/useTranslation";

/** Type-color left border for backlog cards (same palette as BoardTaskCard) */
const TYPE_BORDER_COLORS: Record<number, string> = {
  1: "#D4572B", // Reactive
  2: "#3B82F6", // Preventive
  3: "#8C6BD4", // Predictive
  4: "#2D8C4E", // Inspection
  5: "#6B7280", // GeneralTask
};

/** Priority badge (only High/Critical shown) */
const PRIORITY_BADGE: Record<number, { label: string; bg: string; text: string }> = {
  1: { label: "CRIT", bg: "rgba(212,87,43,0.1)", text: "#D4572B" },
  2: { label: "HIGH", bg: "rgba(232,168,23,0.1)", text: "#C08E12" },
};

interface CalendarBacklogPanelProps {
  workOrders: WorkOrder[];
  onCreateClick: () => void;
  canWrite: boolean;
}

/**
 * 240px backlog panel that slides out when the Backlog pill is active.
 * Shows WOs with status Created(1) or Assigned(2), sorted by boardPosition then createdDate.
 * Cards are FullCalendar-external-draggable: dropping them onto the calendar
 * schedules the WO (handled by BoardCalendarView's eventReceive callback).
 */
const CalendarBacklogPanel: React.FC<CalendarBacklogPanelProps> = ({
  workOrders,
  onCreateClick,
  canWrite,
}) => {
  const { translate } = useTranslation("operations");
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // ─── Kebab menu popover state (WO-RESP-4) ───
  const [kebabOpenId, setKebabOpenId] = useState<string | null>(null);
  const kebabRef = useRef<HTMLDivElement>(null);

  const handleKebabToggle = useCallback((e: React.MouseEvent, woId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setKebabOpenId((prev) => (prev === woId ? null : woId));
  }, []);

  const handleViewDetails = useCallback((e: React.MouseEvent, woId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setKebabOpenId(null);
    navigate(`/operations/work-orders/${woId}`);
  }, [navigate]);

  // Close kebab on click-outside
  useEffect(() => {
    if (!kebabOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) {
        setKebabOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [kebabOpenId]);

  // Filter + sort backlog WOs
  const backlogWOs = useMemo(() => {
    return workOrders
      .filter((wo) =>
        wo.workOrderStatusId === WorkOrderStatusCode.Created ||
        wo.workOrderStatusId === WorkOrderStatusCode.Assigned
      )
      .sort((a, b) => {
        const posA = a.boardPosition ?? Infinity;
        const posB = b.boardPosition ?? Infinity;
        if (posA !== posB) return posA - posB;
        return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
      });
  }, [workOrders]);

  // Initialize FullCalendar external draggable on the container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const draggable = new Draggable(el, {
      itemSelector: ".fc-backlog-event",
      eventData: (eventEl) => {
        const woId = eventEl.getAttribute("data-wo-id");
        const woNum = eventEl.getAttribute("data-wo-number") || "";
        const woTitle = eventEl.getAttribute("data-wo-title") || "";
        const durationMin = parseInt(eventEl.getAttribute("data-wo-duration") || "0", 10);

        return {
          id: `backlog-${woId}`,
          title: `${woNum} — ${woTitle}`,
          duration: durationMin > 0 ? { minutes: durationMin } : { hours: 1 },
          create: true, // tells FC to add a real event on drop
          extendedProps: { backlogWoId: woId },
        };
      },
    });

    return () => draggable.destroy();
  }, [backlogWOs]);

  // Build assignee list for the avatar stack (same logic as BoardTaskCard)
  const getAssignees = (wo: WorkOrder) => {
    if (wo.assignees && wo.assignees.length > 0) return wo.assignees;
    if (wo.assignedToUserName) {
      return [{
        userId: wo.assignedToUserId || "",
        userName: wo.assignedToUserName,
        role: 2,
        roleName: "Lead",
        assignedAt: "",
      }];
    }
    return [];
  };

  return (
    <div
      className="w-[240px] flex-shrink-0 border-r border-mid-border bg-white flex flex-col min-h-0 overflow-hidden"
      style={{ animation: "fadeSlideRight 0.2s ease both" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-mid-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <span
            className="text-sm font-semibold text-mid-charcoal"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {translate("board.backlog")}
          </span>
          <span
            className="min-w-[20px] h-[20px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1.5"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              backgroundColor: "#D4572B",
            }}
          >
            {backlogWOs.length}
          </span>
        </div>
      </div>

      {/* Scrollable card list */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-2 py-2 flex flex-col gap-1.5"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C1C7CD transparent",
        }}
      >
        {backlogWOs.length === 0 ? (
          <div className="text-center py-8">
            <span
              className="text-xs text-mid-muted"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {translate("calendar.noBacklog")}
            </span>
          </div>
        ) : (
          backlogWOs.map((wo) => {
            const borderColor = TYPE_BORDER_COLORS[wo.workOrderTypeId] || TYPE_BORDER_COLORS[5];
            const priorityBadge = PRIORITY_BADGE[wo.priorityCode];
            const assignees = getAssignees(wo);

            return (
              <div
                key={wo.id}
                className="fc-backlog-event group relative flex rounded-lg bg-white border border-mid-border overflow-visible cursor-grab hover:shadow-sm transition-all duration-150 hover:-translate-y-px flex-shrink-0"
                data-wo-id={wo.id}
                data-wo-number={wo.number}
                data-wo-title={wo.title}
                data-wo-duration={wo.estimatedDurationMinutes || ""}
              >
                {/* Type-color left border */}
                <div
                  className="w-[3px] flex-shrink-0 rounded-l-lg"
                  style={{ backgroundColor: borderColor }}
                />

                {/* Card content */}
                <div className="flex-1 flex flex-col gap-1 px-2.5 py-2 min-w-0">
                  {/* Row 1: WO number + priority badge */}
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className="text-[10px] font-medium text-mid-muted truncate"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {wo.number}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {priorityBadge && (
                        <span
                          className="text-[8px] font-semibold px-1.5 py-px rounded"
                          style={{
                            fontFamily: "'Sora', sans-serif",
                            backgroundColor: priorityBadge.bg,
                            color: priorityBadge.text,
                          }}
                        >
                          {priorityBadge.label}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Title */}
                  <span
                    className="text-[12px] font-semibold text-mid-charcoal leading-4 line-clamp-1 pr-4"
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    {wo.title}
                  </span>

                  {/* Row 3: Machine (if any) */}
                  {wo.machineName && (
                    <div className="flex items-center gap-1 overflow-hidden">
                      <MachineIcon sx={{ fontSize: 10, color: "#8C9099", flexShrink: 0 }} />
                      <span
                        className="text-[10px] text-mid-muted truncate"
                        style={{ fontFamily: "'Sora', sans-serif" }}
                      >
                        {wo.machineName}
                      </span>
                    </div>
                  )}

                  {/* Row 4: Assignee + drag handle */}
                  <div className="flex items-center justify-between mt-0.5">
                    {assignees.length > 0 ? (
                      <AssigneeAvatarStack
                        assignees={assignees}
                        maxVisible={2}
                        size={18}
                      />
                    ) : (
                      <span
                        className="text-[9px] text-mid-muted italic"
                        style={{ fontFamily: "'Sora', sans-serif" }}
                      >
                        Unassigned
                      </span>
                    )}
                    <DragIndicatorOutlined
                      sx={{ fontSize: 14, color: "#C1C7CD", flexShrink: 0 }}
                    />
                  </div>
                </div>

                {/* ─── 3-dot kebab menu (WO-RESP-4) ─── */}
                <div
                  ref={kebabOpenId === wo.id ? kebabRef : undefined}
                  className="absolute top-1 right-1"
                >
                  <button
                    onClick={(e) => handleKebabToggle(e, wo.id)}
                    className="w-5 h-5 rounded flex items-center justify-center text-mid-muted opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[#F0F0F0] transition-opacity"
                    aria-label="More actions"
                  >
                    <MoreVertOutlined sx={{ fontSize: 14 }} />
                  </button>

                  {/* Popover menu */}
                  {kebabOpenId === wo.id && (
                    <div
                      className="absolute top-6 right-0 z-50 min-w-[120px] bg-white border border-mid-border rounded-lg shadow-lg py-1"
                      style={{ animation: "fadeIn 0.12s ease both" }}
                    >
                      <button
                        onClick={(e) => handleViewDetails(e, wo.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] font-medium text-mid-charcoal hover:bg-[#F6F9FD] transition-colors"
                        style={{ fontFamily: "'Sora', sans-serif" }}
                      >
                        <OpenInNewOutlined sx={{ fontSize: 13, color: "#8C9099" }} />
                        {translate("calendar.viewDetails")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* + New button */}
      {canWrite && (
        <div className="flex-shrink-0 px-2 pb-2 pt-1 border-t border-mid-border">
          <button
            onClick={onCreateClick}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-mid-muted bg-[#F7F5F0] hover:bg-mid-border hover:text-mid-charcoal transition-colors"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            <AddRounded sx={{ fontSize: 16 }} />
            {translate("quickCreate.addWorkOrder")}
          </button>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeSlideRight {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CalendarBacklogPanel;
